/**
 * FreeTierAdminPanel — Close the free tier and move to exclusivity.
 *
 * Shows current free-tier user count and provides a one-click "Close Free Tier"
 * action that:
 *   1. Sets free_plan_closed = True in app_config (blocks new free signups)
 *   2. Sets free_tier_sunset_date on all existing free-tier accounts (+30 days)
 *      so the existing FreeTierSunsetBanner will prompt them to upgrade.
 *
 * Admin → Finance → Free Tier tab (or Operations)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ShieldOff, Users, AlertTriangle, CheckCircle2, Loader2, ArrowRight, GitMerge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { PanelHeader } from './_shared';
import { freeTierAdminApi } from '@/lib/api';

export default function FreeTierAdminPanel() {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [migPreview, setMigPreview]   = useState(null);
  const [migrating, setMigrating]     = useState(false);
  const [migResult, setMigResult]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        freeTierAdminApi.status(),
        freeTierAdminApi.migrationPreview(),
      ]);
      setStatus(s);
      setMigPreview(p);
    }
    catch { setStatus(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleClose = async () => {
    setClosing(true);
    try {
      const res = await freeTierAdminApi.closeTier();
      toast.success(`Free tier closed. ${res.users_notified} users notified (sunset: ${res.sunset_date}).`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Close failed — please retry.');
    } finally {
      setClosing(false);
    }
  };

  const isClosed = status?.free_plan_closed;

  return (
    <div className="space-y-4" data-testid="free-tier-admin-panel">
      <PanelHeader
        title="Free Tier Management"
        subtitle="Exclusivity mode — close the free tier to new signups and notify existing free users to upgrade."
        onRefresh={load}
      />

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-[13px] py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading status…
        </div>
      ) : (
        <div className="space-y-4">
          {/* Status card */}
          <div className={`rounded-xl border p-5 space-y-3 ${isClosed ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/30' : 'border-amber-200 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/20'}`}>
            <div className="flex items-center gap-2">
              {isClosed
                ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                : <AlertTriangle className="h-5 w-5 text-amber-500" />}
              <span className="font-semibold text-[14px]">
                {isClosed ? 'Free tier is closed — exclusivity mode active' : 'Free tier is currently open'}
              </span>
              <Badge
                variant={isClosed ? 'default' : 'secondary'}
                className={`ml-auto text-[10.5px] ${isClosed ? 'bg-emerald-600' : ''}`}
              >
                {isClosed ? 'Closed' : 'Open'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md bg-background border border-border p-3">
                <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wide text-muted-foreground mb-1">
                  <Users className="h-3.5 w-3.5" /> Current free users
                </div>
                <div className="text-2xl font-bold tabular-nums">
                  {status?.current_free_users ?? '—'}
                </div>
              </div>
              <div className="rounded-md bg-background border border-border p-3">
                <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wide text-muted-foreground mb-1">
                  <ShieldOff className="h-3.5 w-3.5" /> Sunset date
                </div>
                <div className="text-[14px] font-semibold">
                  {status?.free_tier_sunset_date ?? isClosed ? status?.free_tier_sunset_date : 'Not set'}
                </div>
              </div>
            </div>
          </div>

          {/* Strategy context */}
          <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-[12.5px] text-muted-foreground space-y-1.5">
            <p className="font-semibold text-foreground">Exclusivity strategy</p>
            <ul className="list-disc list-inside space-y-1 ml-1">
              <li>New signups will be blocked from the free tier — only paid plans available</li>
              <li>Existing free users receive a 30-day sunset notice via the in-app banner</li>
              <li>After sunset, free accounts lose AI features but retain read-only access to manuscripts</li>
              <li>This creates urgency to upgrade and positions Crown & Rose as a premium tool</li>
            </ul>
          </div>

          {/* Action */}
          {!isClosed ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="gap-1.5"
                  disabled={closing}
                  data-testid="close-free-tier-btn"
                >
                  {closing
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Closing…</>
                    : <><ShieldOff className="h-4 w-4" /> Close Free Tier</>}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Close the free tier?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will block all new free-tier signups and set a 30-day sunset notice for{' '}
                    <strong>{status?.current_free_users}</strong> existing free users. They will see an
                    in-app banner prompting them to upgrade. This action can be reversed by updating
                    the free-tier sunset date in Settings → Config.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Yes, close free tier
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <div className="text-[12.5px] text-muted-foreground">
              Free tier is closed. To re-open, update <strong>free_tier_sunset_date</strong> in{' '}
              Admin → Operations → Settings to a blank value.
            </div>
          )}

          {/* ── Tier Consolidation Migration ── */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-3 mt-2">
            <div className="flex items-center gap-2">
              <GitMerge className="h-4 w-4 text-primary" />
              <span className="font-semibold text-[13.5px]">Tier Consolidation — Author / Publisher / Studio</span>
            </div>
            <p className="text-[12px] text-muted-foreground">
              Migrates <strong>basic / pro / pro_plus → Author</strong> and <strong>premier → Publisher</strong>
              {' '}in the database. Does <em>not</em> change Stripe billing — existing subscribers keep
              their price. New signups see only the 3 new tiers.
            </p>
            {migPreview && migPreview.total_affected > 0 && (
              <div className="rounded-md bg-muted/30 border border-border px-3 py-2 space-y-1">
                <p className="text-[11.5px] font-semibold text-muted-foreground">Migration preview:</p>
                {Object.entries(migPreview.breakdown || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11.5px]">
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span className="font-mono">{k}</span>
                    <span className="text-muted-foreground">— {v} users</span>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground pt-1">
                  Total affected: <strong>{migPreview.total_affected}</strong> users
                </p>
              </div>
            )}
            {migPreview && migPreview.total_affected === 0 && (
              <div className="flex items-center gap-2 text-[12px] text-emerald-600">
                <CheckCircle2 className="h-4 w-4" /> All users are already on the new tier structure.
              </div>
            )}
            {migResult && (
              <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-3 py-2">
                <p className="text-[12px] text-emerald-700 dark:text-emerald-400 font-semibold">
                  Migration complete — {migResult.total_migrated} users updated.
                </p>
              </div>
            )}
            {migPreview && migPreview.total_affected > 0 && !migResult && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    className="gap-1.5"
                    disabled={migrating}
                    data-testid="run-tier-migration-btn"
                  >
                    {migrating
                      ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Migrating…</>
                      : <><GitMerge className="h-3.5 w-3.5" /> Run migration</>}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Run tier consolidation?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will update <strong>{migPreview.total_affected}</strong> user records, mapping
                      old legacy tiers to Author/Publisher. Stripe subscriptions are not affected.
                      This can be re-run safely.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        setMigrating(true);
                        try {
                          const r = await freeTierAdminApi.runMigration();
                          setMigResult(r);
                          toast.success(`Migration complete — ${r.total_migrated} users updated.`);
                          await load();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Migration failed.');
                        } finally { setMigrating(false); }
                      }}
                    >
                      Yes, run migration
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
